import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Toolbar,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  DataGrid, GridColDef, GridRenderCellParams, GridRowParams,
} from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import {
  CfOrder,
  cancelCfOrder,
  changeCfOrderPlan,
  clearCfOrderError,
  closeCfOrder,
  fetchCfOrder,
  fetchCfOrders,
  isTerminal,
  selectCfOrders,
  statusLabel,
} from '../features/cfOrders/cfOrders';
import {
  cancelPrompt, cancelTitle, defaultCfOrderSort, formatMoment, isLive, rowOrderId,
  placedVia,
} from '../features/cfOrders/format';
import { fetchPlans, Plan, selectPlans } from '../features/plans/plans';
import { networkOf } from '../features/plans/planNetwork';
import CfOrderCard from './CfOrderCard';
import { statusColour } from './cfOrderStatus';

/**
 * Community Fibre orders. Each row says just enough to find an order and act
 * on it; clicking it opens the order as a card with everything else.
 */
const CfOrders = () => {
  const dispatch = useAppDispatch();
  const {
    orders, status, error, cancelling, cancelError, changing, changeError,
    openId, detail, detailStatus, detailError,
  } = useAppSelector(selectCfOrders);
  const plans = useAppSelector(selectPlans);
  const [openOnly, setOpenOnly] = useState(false);
  const [confirming, setConfirming] = useState<CfOrder | null>(null);
  // The live service whose plan is being changed, and the plan picked for it.
  const [changingRow, setChangingRow] = useState<CfOrder | null>(null);
  const [newPlan, setNewPlan] = useState('');

  useEffect(() => {
    dispatch(fetchCfOrders({ openOnly }));
  }, [dispatch, openOnly]);
  // The plans a service can move to come from the catalogue.
  useEffect(() => {
    if (plans.length === 0) dispatch(fetchPlans());
  }, [dispatch, plans.length]);

  // The grid sorts; it opens on the most recently placed order.
  const rows = orders;
  const openRow = orders.find((o) => o.order_id === openId) ?? null;

  const onOpen = (row: CfOrder) => dispatch(fetchCfOrder(row.order_id));

  const askToCancel = (row: CfOrder) => {
    dispatch(clearCfOrderError());
    setConfirming(row);
  };

  const onConfirmCancel = async () => {
    if (!confirming) return;
    const result = await dispatch(cancelCfOrder(confirming.order_id));
    // On a refusal the dialog stays open with the reason in it, rather than
    // closing onto a page whose alert sits behind the card.
    if (cancelCfOrder.fulfilled.match(result)) {
      setConfirming(null);
    }
  };

  const confirmBusy = confirming !== null && cancelling === confirming.order_id;
  // Nothing goes to Community Fibre for an order they never saw, so the
  // button only says it asks them when it does.
  const asksThem = Boolean(confirming?.community_fibre_order_id);
  const confirmingLive = confirming !== null && isLive(confirming);
  let confirmLabel = 'Cancel order';
  if (confirmingLive) confirmLabel = 'Ask them to disconnect';
  else if (asksThem) confirmLabel = 'Ask them to cancel';
  if (confirmBusy) confirmLabel = asksThem || confirmingLive ? 'Asking them…' : 'Cancelling…';

  const askToChange = (row: CfOrder) => {
    dispatch(clearCfOrderError());
    setNewPlan('');
    setChangingRow(row);
  };

  const onConfirmChange = async () => {
    if (!changingRow || !newPlan) return;
    const result = await dispatch(changeCfOrderPlan({ orderId: changingRow.order_id, planId: newPlan }));
    if (changeCfOrderPlan.fulfilled.match(result)) {
      setChangingRow(null);
    }
  };

  const changeBusy = changingRow !== null && changing === changingRow.order_id;
  // Community Fibre plans other than the one the service is on, fastest last.
  const planChoices: Plan[] = plans
    .filter((p) => networkOf(p) === 'communityfibre' && p.id !== changingRow?.plan_id)
    .sort((a, b) => (a.speed - b.speed) || a.id.localeCompare(b.id));

  const columns: GridColDef<CfOrder>[] = [
    {
      field: 'address',
      headerName: 'Address and building',
      flex: 2,
      minWidth: 260,
      renderCell: (params: GridRenderCellParams<CfOrder>) => (
        <Box sx={{ py: 1, minWidth: 0 }}>
          {/* A real button, so the row can be reached and opened by keyboard
              and is announced as something that opens. */}
          <ButtonBase
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onOpen(params.row);
            }}
            sx={{
              display: 'block',
              textAlign: 'left',
              maxWidth: '100%',
              borderRadius: 0.5,
              '&:hover .cf-address': { textDecoration: 'underline' },
              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
            }}
          >
            <Typography
              className="cf-address"
              variant="body2"
              noWrap
              color={params.row.address ? 'text.primary' : 'text.secondary'}
            >
              {params.row.address || 'No address'}
            </Typography>
          </ButtonBase>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            {params.row.building_gnaf_id || params.row.service_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'community_fibre_order_id',
      headerName: 'Order',
      width: 190,
      renderCell: (params: GridRenderCellParams<CfOrder>) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color={params.row.community_fibre_order_id ? 'text.primary' : 'text.secondary'}
            sx={{ fontFamily: params.row.community_fibre_order_id ? 'monospace' : undefined }}
          >
            {rowOrderId(params.row)}
          </Typography>
          {/* Which API placed it, and the products it named: what their
              support ask about first. */}
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {placedVia(params.row)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 190,
      renderCell: (params: GridRenderCellParams<CfOrder>) => (
        <Chip
          size="small"
          label={statusLabel(params.row.status)}
          color={statusColour(params.row.status)}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Placed',
      width: 170,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams<CfOrder>) => (
        <Typography variant="body2">{formatMoment(params.row.created_at)}</Typography>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Last change',
      width: 170,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams<CfOrder>) => (
        <Typography variant="body2" color="text.secondary">{formatMoment(params.row.updated_at)}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 290,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<CfOrder>) => {
        const busy = cancelling === params.row.order_id || changing === params.row.order_id;
        const live = isLive(params.row);
        let cancelText = live ? 'Disconnect' : 'Cancel order';
        if (busy) cancelText = 'Asking them…';
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {params.row.changeable && (
              <Button
                size="small"
                variant="outlined"
                aria-label={`Change plan of ${params.row.service_number ?? rowOrderId(params.row)}`}
                disabled={busy}
                onClick={(e) => {
                  // The row opens the card; this button only changes.
                  e.stopPropagation();
                  askToChange(params.row);
                }}
              >
                Change plan
              </Button>
            )}
            {params.row.cancellable && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                aria-label={live
                  ? `Disconnect service ${params.row.service_number ?? rowOrderId(params.row)}`
                  : `Cancel order ${rowOrderId(params.row)}`}
                disabled={busy}
                onClick={(e) => {
                  // The row opens the card; this button only cancels.
                  e.stopPropagation();
                  askToCancel(params.row);
                }}
              >
                {cancelText}
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  const open = orders.filter((o) => !isTerminal(o.status)).length;

  return (
    <Box sx={{ p: 2 }}>
      <Toolbar disableGutters sx={{ gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Community Fibre orders
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {open}
          {' '}
          open
        </Typography>
        <FormControlLabel
          control={<Switch checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
          label="Only open"
        />
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => dispatch(fetchCfOrders({ openOnly }))}
        >
          Refresh
        </Button>
      </Toolbar>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click an order to see everything about it. Community Fibre build the
        service themselves, so each order is followed until it is live,
        cancelled or refused. A live service can be moved to another plan or
        disconnected; each is an order of theirs against it, followed the same way.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearCfOrderError())}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          getRowId={(row: CfOrder) => row.order_id}
          getRowHeight={() => 'auto'}
          loading={status === 'loading'}
          disableRowSelectionOnClick
          onRowClick={(params: GridRowParams<CfOrder>) => {
            // Selecting an id to copy it is a click too; it should not open
            // the card over what is being copied.
            if (window.getSelection()?.toString()) return;
            onOpen(params.row);
          }}
          onCellKeyDown={(params, event) => {
            // Enter opens the card for someone moving through the list by
            // keyboard, as a click does.
            if (event.key === 'Enter' && params.field !== 'actions') {
              onOpen(params.row as CfOrder);
            }
          }}
          sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          localeText={{
            noRowsLabel: status === 'failed'
              ? 'Could not load the orders'
              : 'No Community Fibre orders yet',
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: defaultCfOrderSort },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Paper>

      <CfOrderCard
        row={openRow}
        detail={detail}
        loading={detailStatus === 'loading'}
        error={detailError}
        cancelling={openRow !== null && cancelling === openRow.order_id}
        changing={openRow !== null && changing === openRow.order_id}
        onClose={() => dispatch(closeCfOrder())}
        onCancel={askToCancel}
        onChange={askToChange}
      />

      <Dialog open={changingRow !== null} onClose={() => !changeBusy && setChangingRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          Change plan of
          {' '}
          {changingRow?.service_number ?? changingRow?.address ?? 'this service'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Now on
            {' '}
            {changingRow?.plan_id ?? 'an unknown plan'}
            . Community Fibre will be asked to move the service to the plan
            picked here; it stays on its current plan until they apply the
            change. The subscription is not touched: change that in Chargebee.
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="cf-new-plan-label">New plan</InputLabel>
            <Select
              labelId="cf-new-plan-label"
              id="cf-new-plan"
              label="New plan"
              value={newPlan}
              onChange={(e) => setNewPlan(String(e.target.value))}
              disabled={changeBusy}
            >
              {planChoices.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  {' '}
                  (
                  {p.id}
                  )
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {planChoices.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No other Community Fibre plans in the catalogue.
            </Typography>
          )}
          {changeError && (
            <Alert severity="error" sx={{ mt: 2 }}>{changeError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangingRow(null)} disabled={changeBusy}>Keep it</Button>
          <Button
            variant="contained"
            onClick={onConfirmChange}
            disabled={changeBusy || !newPlan}
            startIcon={changeBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {changeBusy ? 'Asking them…' : 'Ask them to change it'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirming !== null} onClose={() => !confirmBusy && setConfirming(null)}>
        <DialogTitle>{confirming && cancelTitle(confirming)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirming && cancelPrompt(confirming)}
            {confirming?.community_fibre_order_id && !confirmingLive && (
              <>
                {' '}
                They accept the request and apply it in their own time, so the
                order stays open until they confirm.
              </>
            )}
            {' '}
            The subscription is not touched: cancel that in Chargebee.
          </DialogContentText>
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>{cancelError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={confirmBusy}>Keep it</Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmCancel}
            disabled={confirmBusy}
            startIcon={confirmBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CfOrders;
